import { SurveyAnswer, SurveyEvent, SurveyPresentation } from './types';

export const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value) &&
  Object.getPrototypeOf(value) === Object.prototype;
export const isText = (value: unknown): value is string => typeof value === 'string';
export const isIdentifier = (value: unknown): value is string =>
  isText(value) && value.trim().length > 0 && value.length <= 5000;
export const isRevision = (value: unknown): value is number =>
  Number.isSafeInteger(value) && Number(value) > 0;
export const hasOnly = (value: Record<string, unknown>, keys: string[]) =>
  Object.keys(value).every((key) => keys.includes(key));
export const outcomes = ['skip', 'prefer_not_to_say', 'do_not_know'];
export const controlActions = ['continue', 'goBack', 'useSimplerWording', 'useStandardWording', 'pause', 'resume', 'end', 'skipOnboardingSurvey'];
const submitActions = ['submitAnswer', 'selectRoute', 'decideConsent'];
const actionsWithOptionalLabels = [...submitActions, 'goBack', 'skipOnboardingSurvey', 'requestHelp'];
const footerActions = [...controlActions, ...submitActions, 'requestHelp'];
const helpPresentationTypes = ['surveyQuestion', 'surveyInformation', 'surveyRoute', 'surveyConsent'];
const includes = (values: string[], value: unknown) => isText(value) && values.includes(value);
const unique = (values: unknown[]) => new Set(values).size === values.length;

export const validAnswer = (value: unknown): value is SurveyAnswer => {
  if (!isRecord(value)) return false;
  switch (value.type) {
    case 'singleChoice': return hasOnly(value, ['type', 'choiceId', 'specifyText']) && isIdentifier(value.choiceId) &&
      (!('specifyText' in value) || isIdentifier(value.specifyText));
    case 'route': return hasOnly(value, ['type', 'choiceId']) && isIdentifier(value.choiceId);
    case 'multiChoice': return hasOnly(value, ['type', 'choiceIds']) && Array.isArray(value.choiceIds) &&
      value.choiceIds.length > 0 && value.choiceIds.every(isIdentifier) && unique(value.choiceIds);
    case 'freeText': return hasOnly(value, ['type', 'text']) && isIdentifier(value.text);
    case 'nonAnswer': return hasOnly(value, ['type', 'outcome']) && includes(outcomes, value.outcome);
    case 'consent': return hasOnly(value, ['type', 'accepted']) && typeof value.accepted === 'boolean';
    default: return false;
  }
};

const presentationKeys = ['schemaVersion', 'messageType', 'formId', 'presentationRevision', 'status', 'surveyId', 'surveyVersion', 'blockId', 'blockType', 'content', 'language', 'validationText', 'options', 'nonAnswerOptions', 'footerActions', 'notice', 'nextStepsContent', 'estimatedDurationText', 'questionNumber', 'estimatedMinutes', 'inputType', 'required', 'maxLength', 'multiline', 'help', 'consent'];
const typeBlocks: Record<string, string[]> = {
  surveyQuestion: ['question', 'languageSelection', 'wordingSelection'], surveyInformation: ['information', 'sectionBreak'],
  surveyRoute: ['route'], surveyConsent: ['consent'], surveyCompletion: ['completion'],
  surveyState: ['question', 'route', 'consent', 'completion', 'information', 'sectionBreak'],
};

export const validPresentation = (value: unknown): value is SurveyPresentation => {
  if (!isRecord(value) || !hasOnly(value, presentationKeys) || value.schemaVersion !== 1 ||
      !isText(value.messageType) || !includes(typeBlocks[value.messageType] ?? [], value.blockType) ||
      !['formId', 'surveyId', 'surveyVersion', 'blockId'].every((key) => isIdentifier(value[key])) ||
      !isRevision(value.presentationRevision) ||
      !['content', 'language', 'validationText'].every((key) => isText(value[key]))) return false;
  if (value.messageType === 'surveyState' ? !includes(['paused', 'ended'], value.status) :
      value.status !== (value.messageType === 'surveyCompletion' ? 'completed' : 'pending')) return false;
  for (const key of ['notice', 'nextStepsContent', 'estimatedDurationText']) {
    if (key in value && !isText(value[key])) return false;
  }
  for (const key of ['questionNumber', 'estimatedMinutes']) {
    if (key in value && (!Number.isSafeInteger(value[key]) || Number(value[key]) < 0)) return false;
  }
  if (!Array.isArray(value.options) || !value.options.every((option) => isRecord(option) &&
      hasOnly(option, ['id', 'label', 'requiresFreeText']) && isIdentifier(option.id) && isIdentifier(option.label) &&
      (!('requiresFreeText' in option) || typeof option.requiresFreeText === 'boolean')) ||
      !unique(value.options.map((option) => option.id))) return false;
  if (!Array.isArray(value.nonAnswerOptions) || !value.nonAnswerOptions.every((option) => isRecord(option) &&
      hasOnly(option, ['outcome', 'label']) && includes(outcomes, option.outcome) && isIdentifier(option.label)) ||
      !unique(value.nonAnswerOptions.map((option) => option.outcome))) return false;
  if (!Array.isArray(value.footerActions) || !value.footerActions.every((footer) => isRecord(footer) &&
      hasOnly(footer, ['action', 'label', 'enabled']) && includes(footerActions, footer.action) &&
      typeof footer.enabled === 'boolean' &&
      (actionsWithOptionalLabels.includes(String(footer.action)) ? !('label' in footer) || isIdentifier(footer.label) : isIdentifier(footer.label))) ||
      !unique(value.footerActions.map((footer) => footer.action))) return false;
  if ('help' in value) {
    const help = value.help;
    if (!includes(helpPresentationTypes, value.messageType) || !isRecord(help) ||
        typeof help.available !== 'boolean' || !includes(['configured', 'generative'], help.mode)) return false;
    if (help.mode === 'configured') {
      if (!hasOnly(help, ['available', 'mode', 'remaining', 'limit']) ||
          ('remaining' in help && help.remaining !== null) || ('limit' in help && help.limit !== null)) return false;
    } else if (!hasOnly(help, ['available', 'mode', 'remaining', 'limit', 'started']) || !Number.isInteger(help.remaining) ||
        !Number.isInteger(help.limit) || Number(help.limit) < 1 || Number(help.limit) > 5 ||
      Number(help.remaining) < 0 || Number(help.remaining) > Number(help.limit) ||
      ('started' in help && typeof help.started !== 'boolean')) return false;
    const helpAction = value.footerActions.find((footer) => footer.action === 'requestHelp');
    if (help.mode === 'configured') {
      const validHelpAction = help.available
        ? !!helpAction && helpAction.enabled
        : !helpAction || !helpAction.enabled;
      if (!validHelpAction) return false;
    } else {
      const validInitialHelpAction = help.started === true
        ? !helpAction || !helpAction.enabled && !help.available
        : help.started === false
          ? help.available ? !!helpAction && helpAction.enabled : !helpAction || !helpAction.enabled
          : !helpAction;
      if (!validInitialHelpAction) return false;
    }
  } else if (value.footerActions.some((footer) => footer.action === 'requestHelp')) return false;
  if (value.messageType === 'surveyQuestion') {
    if (!includes(['singleChoice', 'multiChoice', 'freeText'], value.inputType) || typeof value.required !== 'boolean') return false;
    if (value.inputType === 'freeText' ? value.maxLength !== 5000 || value.multiline !== true || value.options.length !== 0 :
        'maxLength' in value || 'multiline' in value || value.options.length === 0) return false;
    if (includes(['languageSelection', 'wordingSelection'], value.blockType) &&
      (value.questionNumber !== 0 || value.inputType !== 'singleChoice' ||
      value.required !== true || value.nonAnswerOptions.length !== 0 ||
      !value.footerActions.some((footer) => footer.action === 'submitAnswer') ||
      value.footerActions.some((footer) => !['submitAnswer', 'requestHelp', 'skipOnboardingSurvey', 'goBack'].includes(String(footer.action))))) return false;
  } else if ('inputType' in value || 'maxLength' in value || 'multiline' in value || 'required' in value) return false;
  if (value.messageType === 'surveyRoute' && value.options.length === 0) return false;
  if ('consent' in value) {
    const consent = value.consent;
    if (!isRecord(consent) || !hasOnly(consent, ['itemId', 'version', 'classification', 'required', 'explanation', 'acceptedLabel', 'declinedLabel']) ||
        !['itemId', 'version', 'acceptedLabel', 'declinedLabel'].every((key) => isIdentifier(consent[key])) ||
        !isText(consent.explanation) || typeof consent.required !== 'boolean' ||
        !includes(['consent', 'acknowledgement', 'understanding', 'preference'], consent.classification)) return false;
  }
  if (value.messageType === 'surveyConsent' && !value.consent) return false;
  const allowed = value.status === 'ended' || value.status === 'completed' ? [] : value.status === 'paused' ? ['resume'] :
    [...controlActions.filter((action) => action !== 'resume'), 'requestHelp',
      ...(value.messageType === 'surveyQuestion' ? ['submitAnswer'] : value.messageType === 'surveyRoute' ? ['selectRoute'] : value.messageType === 'surveyConsent' ? ['decideConsent'] : [])];
  return value.footerActions.every((footer) => allowed.includes(footer.action));
};

export const utf8Size = (text: string) => {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(text).length;
  return encodeURIComponent(text).replace(/%[A-F\d]{2}/g, 'x').length;
};

export const parseSurveyEvent = (input: unknown): SurveyEvent | undefined => {
  try {
    if (!isRecord(input) || utf8Size(JSON.stringify(input)) > 65536 || input.schemaVersion !== 1 || input.target !== 'interactive' ||
        !isIdentifier(input.chatSessionId) || !isIdentifier(input.responseId) || !isRecord(input.payload)) return undefined;
    const payload = input.payload;
    if (input.messageType !== 'surveyInteractionResult') {
      return hasOnly(input, ['schemaVersion', 'target', 'messageType', 'eventId', 'chatSessionId', 'responseId', 'payload']) &&
        validPresentation(payload) && payload.messageType === input.messageType && payload.formId === input.eventId
        ? JSON.parse(JSON.stringify(input)) as SurveyEvent : undefined;
    }
    if (!hasOnly(input, ['schemaVersion', 'target', 'messageType', 'chatSessionId', 'responseId', 'payload']) ||
        !hasOnly(payload, ['interactionId', 'formId', 'presentationRevision', 'status', 'code', 'replacementFormId', 'replacementRevision', 'acceptedAnswer', 'provisionalAnswer']) ||
        !isIdentifier(payload.interactionId) || !isIdentifier(payload.formId) || !isRevision(payload.presentationRevision) ||
        !includes(['accepted', 'replayed', 'rejected', 'retryable'], payload.status) ||
        ('code' in payload && !isIdentifier(payload.code))) return undefined;
    const successful = payload.status === 'accepted' || payload.status === 'replayed';
    if (('replacementFormId' in payload) !== ('replacementRevision' in payload) ||
        ('replacementFormId' in payload && (!successful || !isIdentifier(payload.replacementFormId) || !isRevision(payload.replacementRevision))) ||
        ('acceptedAnswer' in payload && (!successful || !validAnswer(payload.acceptedAnswer))) ||
        ('provisionalAnswer' in payload && (!successful || 'acceptedAnswer' in payload ||
          !validAnswer(payload.provisionalAnswer) || payload.provisionalAnswer.type !== 'freeText'))) return undefined;
    return JSON.parse(JSON.stringify(input)) as SurveyEvent;
  } catch {
    return undefined;
  }
};
